import { deleteUser, updateUser, updateUserAvatar, useUser } from '@gik/api/users/user';
import { auth } from '@gik/auth';
import LoginProviders from '@gik/auth/components/ExternalLogin/LoginProviders';
import useLoginProvider from '@gik/auth/hooks/useLoginProvider';
import { LoginProvider } from '@gik/auth/utils/LoginProvider';
import { optimisticUpdate } from '@gik/core/api';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { defaultUserImagePath, timeoutDefaultValue } from '@gik/core/constants';
import routes from '@gik/core/routes';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import { navigateTo } from '@gik/core/utils/navigateTo';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Avatar } from '@gik/ui-legacy/Avatar';
import { Backdrop } from '@gik/ui-legacy/Backdrop';
import { Button } from '@gik/ui-legacy/Button';
import type { ButtonElement } from '@gik/ui-legacy/Button/ButtonProps';
import { FlippableCard } from '@gik/ui-legacy/FlippableCard';
import type { FormRef, FormSchemaEntry, ValidationOptions } from '@gik/ui-legacy/Form';
import { Form, FormField, validateForm } from '@gik/ui-legacy/Form';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon';
import { Text } from '@gik/ui-legacy/Text/Text';
import { Tooltip } from '@gik/ui-legacy/Tooltip';
import { UI } from '@gik/ui-legacy/UIManager';
import { LightboxMedia } from '@gik/ui-legacy/UIManager/UILightboxTypes';
import { BillingAddressBox } from '@gik/ui-legacy/gik/BillingAddressBox/BillingAddressBox';
import { openAddEditUserBillingAddressModal } from '@gik/ui-legacy/gik/BillingAddressBox/openEditUserBillingAddressModal';
import { openImagePickerModal } from '@gik/ui-legacy/gik/ImagePickerModal';
import { UserSettingsFormFieldNames } from '@gik/user-profile/types/UserSettings';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import PlusIcon from '@heroicons/react/outline/PlusIcon';
import CameraIcon from '@heroicons/react/solid/CameraIcon';
import React from 'react';
import type Geosuggest from 'react-geosuggest';
import { useUserWallets } from '../../hooks/useUserWallets';
import UserSettingsSkeleton from './UserSettingsSkeleton';

const blockName = 'user-settings';

const FieldNames = UserSettingsFormFieldNames;

type EditingUserFormType = {
  [FieldNames.FirstName]: string;
  [FieldNames.LastName]: string;
  [FieldNames.EmailAddress]: string;
  [FieldNames.CityAndState]: string;
};

function UserSettingsComp() {
  const bem = bemBlock(blockName);
  const userStore = useUserStore();
  const userId = userStore.id;
  const { data: user, error: userError, mutate: mutateUser } = useUser(userId);
  const provider = useLoginProvider(user?.emailAddress);

  const { data: wallets, error } = useUserWallets(userId);

  const [isFlipped, setFlipped] = React.useState<boolean>(false);
  const [newAvatarPreview, setNewAvatarPreview] = React.useState(null);

  const formRef = React.useRef<FormRef>(null);
  const geoSuggestRef = React.useRef<Geosuggest>(null);

  const onClickEditProfile = (): void => {
    setFlipped(true);
    setNewAvatarPreview(null);
  };

  function finishUserEditing() {
    setFlipped(false);
    setNewAvatarPreview(null);
  }

  const cancelUserEditing = (): void => {
    finishUserEditing();

    formRef.current?.resetForm();
    if (typeof geoSuggestRef.current?.setState === 'function') {
      geoSuggestRef.current?.setState(state => ({
        ...state,
        userInput: getGeosuggestInitialInput(),
      })); // TODO: need to find a way to hook this up with the form generator's resetForm handle
    }
  };

  const onClickChangePassword = (): void => {
    if (provider !== LoginProvider.EMAIL) return;
    auth.resetPassword({ email: user.emailAddress });
  };

  const handleLogout = (): void => {
    // mark account as deleted
    // the user should be logged out after navigation to homepage is completed
    useUserStore.getState().setShouldLogout(true);

    navigateTo(routes.home);
  };

  const onClickDeleteAccount = async () => {
    if (wallets?.length > 0) {
      const recipientWallets = wallets.filter(w => w.isUserRecipient);

      if (recipientWallets.length > 0) {
        // GIK-10301: Check whether all recipient wallets can be safely transferred to the page owner.
        // Blocking cases: user IS the page owner (can't transfer to self), or the page owner's
        // account is already deactivated.
        const canTransfer = recipientWallets.every(w => !w.isUserPageOwner && w.pageOwnerIsActive);

        if (!canTransfer) {
          await UI.alert(
            <div>
              <p>
                We are unable to deactivate your account as you are designated as the Recipient for an active Wallet.
              </p>
              <p className="tw-mt-4">Please deactivate your Wallet before deactivating your Give InKind account.</p>
            </div>,
            {
              title: 'Unsuccessful Deactivation',
              okText: 'Back To Profile',
              okButtonProps: { variant: 'default' },
            }
          );
          navigateTo(routes.userProfile);
          return;
        }

        // All recipient wallets can be transferred — confirm and proceed.
        const response = await UI.confirm(
          <p>
            Are you sure you want to deactivate your account?
            <p className="tw-mt-4">
              You will no longer have access to any Wallets designated for this account, including any gifts they
              contain.
            </p>
          </p>,
          {
            title: 'Deactivate Account',
            okText: 'Deactivate',
            okButtonProps: { variant: 'danger' },
          }
        );

        if (!response) return;
      } else {
        // GIK-9790: Manager-only wallets (user is not a recipient) — allow with a warning.
        const response = await UI.confirm(
          <>
            <p>Are you sure you want to deactivate your account?</p>
            <p className="tw-mt-4">
              You will lose access to any Wallets you manage, along with the gifts they contain.
            </p>
          </>,
          {
            title: 'Deactivate Account',
            okText: 'Deactivate',
            okButtonProps: { variant: 'danger' },
          }
        );

        if (!response) return;
      }

      UI.loadingSpinnerOverlay();
      try {
        await deleteUser(userId);
      } catch {
        UI.closeAllDialogs();
        await UI.alert(<p>We were unable to deactivate your account. Please try again or contact support.</p>, {
          title: 'Deactivation Failed',
          okText: 'OK',
          okButtonProps: { variant: 'default' },
        });
        return;
      }
      UI.closeAllDialogs();
      await UI.success('You have successfully deactivated your account.', {
        title: 'Confirmed',
        okText: 'Go to homepage',
        okButtonProps: { variant: 'primary' },
      });

      // mark account as deleted
      // the user should be logged out after navigation to homepage is completed
      useUserStore.getState().setShouldLogout(true);
      navigateTo(routes.home);
      return;
    }

    // No wallets — simple confirmation.
    const response = await UI.confirm(<p>Are you sure you want to deactivate your account?</p>, {
      title: 'Confirm Deactivation',
      okText: 'Deactivate',
      okButtonProps: { variant: 'danger' },
    });

    if (response) {
      UI.loadingSpinnerOverlay();
      try {
        await deleteUser(userId);
      } catch {
        UI.closeAllDialogs();
        await UI.alert(<p>We were unable to deactivate your account. Please try again or contact support.</p>, {
          title: 'Deactivation Failed',
          okText: 'OK',
          okButtonProps: { variant: 'default' },
        });
        return;
      }
      UI.closeAllDialogs();
      await UI.success('You have successfully deactivated your account.', {
        title: 'Confirmed',
        okText: 'Go to homepage',
        okButtonProps: { variant: 'primary' },
      });

      // mark account as deleted
      // the user should be logged out after navigation to homepage is completed
      useUserStore.getState().setShouldLogout(true);

      navigateTo(routes.home);
    }
  };

  const saveUserInfo = React.useCallback(
    async (values: EditingUserFormType, croppedImageProp?: string): Promise<void> => {
      const transferObject: {
        firstName: string;
        lastName: string;
        emailAddress: string;
        city?: string;
        state?: string;
      } = {
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.emailAddress,
      };

      if (values?.cityAndState?.length > 0 && values?.cityAndState?.split(',')?.length > 1) {
        const split = values.cityAndState.split(',');
        transferObject.city = split[0].trim();
        transferObject.state = split[1].trim();
      } else {
        transferObject.city = null;
        transferObject.state = 'NA';
      }

      const updatedUser = { ...user, ...transferObject };

      if (newAvatarPreview || croppedImageProp) {
        // note: this lib may not be imported for SSR
        // eslint-disable-next-line
        const dataURLtoBlob = require('blueimp-canvas-to-blob');
        await updateUserAvatar(userId, dataURLtoBlob(newAvatarPreview || croppedImageProp));
        mutateUser();
      }

      optimisticUpdate(updatedUser, null, mutateUser, () => updateUser(userId, updatedUser));

      finishUserEditing();
    },
    [mutateUser, newAvatarPreview, user, userId]
  );

  const onChangeAvatarClicked = React.useCallback(() => {
    openImagePickerModal({
      title: 'Edit photo',
      text: 'Upload a photo for your profile.',
      fallback: user?.avatarURL || defaultUserImagePath,
      onChange: croppedImage => {
        setNewAvatarPreview(croppedImage);
        setTimeout(() => {
          saveUserInfo(formRef.current?.values as EditingUserFormType, croppedImage);
        }, timeoutDefaultValue);
      },
    });
  }, [saveUserInfo, user?.avatarURL]);

  const getGeosuggestInitialInput = () => {
    if (!user) {
      return null;
    }
    return user.city?.length && user.state !== 'NA' ? `${user.city}, ${user.state}` : '';
  };

  // TODO: remove duplication between signup form schema and user settings schema
  const getSchema: (loginProvider: LoginProvider) => FormSchemaEntry[] = loginProvider => {
    const providerIcon = LoginProviders.find(provider => provider.value === loginProvider)?.icon;

    return [
      {
        name: FieldNames.FirstName,
        type: 'text',
        minLength: 2,
        maxLength: 127,
        required: true,
        props: {
          variant: 'default-solid',
          placeholder: 'Your First name',
        },
      },
      {
        name: FieldNames.LastName,
        type: 'text',
        //required: true, // some users do not want to provide last name
        minLength: 2,
        maxLength: 127,
        props: {
          variant: 'default-solid',
          placeholder: 'Your Last name',
        },
      },
      {
        name: FieldNames.EmailAddress,
        type: 'email',
        required: true,
        props: {
          variant: 'default-solid',
          placeholder: 'Your Email Address',
          disabled: loginProvider !== LoginProvider.EMAIL,
          prepend:
            loginProvider !== LoginProvider.EMAIL && Boolean(providerIcon) ? (
              <Tooltip
                text={
                  <span>
                    Connected to <span className="tw-capitalize">{loginProvider}</span>
                  </span>
                }
              >
                <div>
                  <SvgIcon
                    className={bem('login-provider-icon')}
                    Icon={LoginProviders.find(provider => provider.value === loginProvider)?.icon}
                  />
                </div>
              </Tooltip>
            ) : null,
        },
      },
      {
        name: FieldNames.CityAndState,
        type: 'geosuggest',
        props: {
          className: 'gik-geosuggest--variant-default-solid',
          placeholder: 'What city are you located in?',
          initialValue: getGeosuggestInitialInput(),
          ref: geoSuggestRef,
        },
      },
    ];
  };

  const schema = getSchema(provider);

  const isLoading = !user;
  const isError = isLoading && userError;

  // TODO: figure out billing address existance based on user fields.
  const hasBillingAddress = !!user?.billingAddresses?.length;

  const handleAddBillingAddress = React.useCallback(async () => {
    const response = await openAddEditUserBillingAddressModal({
      user,
      billingAddress: user.billingAddresses?.[0],
    });
    if (response) mutateUser();
  }, [mutateUser, user]);

  const handleClickUserAvatar = React.useCallback(() => {
    UI.lightbox([
      {
        mediaType: LightboxMedia.IMAGE,
        url: user.avatarURL || defaultUserImagePath,
        caption: `${user.firstName} ${user.lastName}`.trim(),
      },
    ]);
  }, [user]);

  const handleClickUserAvatarButton = React.useCallback(
    (ev: React.MouseEvent<ButtonElement>) => {
      ev.stopPropagation();
      onChangeAvatarClicked();
    },
    [onChangeAvatarClicked]
  );

  const validate = async (values: EditingUserFormType, opts: ValidationOptions) => {
    const validation = await validateForm(schema, values, opts);

    if (values.cityAndState === 'invalid') {
      // Only block when the user has typed something but not selected a suggestion.
      // If the geosuggest input is empty (user cleared the field), treat as valid —
      // an empty city is allowed and saveUserInfo handles it gracefully.
      const geoSuggest = geoSuggestRef.current as
        | (Geosuggest & { state?: { userInput?: string } })
        | null;
      const userInput = (geoSuggest?.state?.userInput ?? '').trim();
      if (userInput.length > 0) {
        validation.cityAndState = { message: '' };
      }
    }

    return validation;
  };

  const handleEditAddSuccessBillingDetails = () => {
    mutateUser();
  };
  const handleDeleteBillingDetails = () => {
    mutateUser();
  };

  if (isLoading || isError) return <UserSettingsSkeleton />;

  return (
    <Backdrop isOpen={isFlipped} liftChildrenOnTrigger={true}>
      <FlippableCard
        className={bem('card-wrapper')}
        isFlipped={isFlipped}
        frontContent={
          <div className={`${bem()} ${bem('base')}`} style={{ visibility: isFlipped ? 'hidden' : 'visible' }}>
            <section className={bem('avatar-container')}>
              <Avatar
                className={bem('avatar')}
                rounded
                size={'3xl'}
                imageUrl={newAvatarPreview || user?.avatarThumbMediumURL || defaultUserImagePath}
                loading={!user}
                onClick={handleClickUserAvatar}
                toolbar={
                  <Button
                    circle
                    variant="default"
                    className={bem('avatar-button')}
                    onClick={handleClickUserAvatarButton}
                  >
                    <CameraIcon />
                  </Button>
                }
              />
            </section>
            <div className={bem('info')}>
              <section className={bem('full-name')}>
                <Text>
                  {user.firstName} {user.lastName}
                </Text>
              </section>
              <section className={bem('email-address')}>{user.emailAddress}</section>
              <section className={bem('city-state')}>
                <div className="user-details-typography">{getGeosuggestInitialInput()}</div>
              </section>
            </div>

            <div className={bem('filler', null, 'hidden md:flex')}></div>

            <section className={bem('buttons')}>
              <div className={bem('edit-button')} onClick={() => onClickEditProfile()}>
                Edit profile
              </div>

              {provider === LoginProvider.EMAIL && (
                <div className={bem('password-button')} onClick={() => onClickChangePassword()}>
                  Change password
                </div>
              )}

              <div className={bem('logout-button')} onClick={() => handleLogout()}>
                Logout
              </div>

              <div className={bem('delete-button')} onClick={() => onClickDeleteAccount()}>
                Delete account
              </div>
            </section>
          </div>
        }
        backContent={
          <div className={`${bem('back')} ${bem('base')}`} style={{ visibility: isFlipped ? 'visible' : 'hidden' }}>
            <section
              className={`${bem('avatar-container')} ${bem('avatar-container', 'editable')}`}
              onClick={() => onChangeAvatarClicked()}
            >
              <Avatar
                className={bem('avatar')}
                rounded
                size={'3xl'}
                imageUrl={newAvatarPreview ? newAvatarPreview : user?.avatarThumbMediumURL || defaultUserImagePath}
                loading={!user}
              />
              <SvgIcon Icon={PhotographIcon} />
            </section>

            {/*TODO: add a cancelButton prop to form and then we can use a full schema rather than hybrid form*/}
            <Form
              id="user-settings"
              restoreAfterUpdate
              className={bem('form')}
              ref={formRef}
              schema={schema}
              vertical
              validate={validate}
              onSubmit={(values: EditingUserFormType) => saveUserInfo(values)}
              initialValues={{
                [FieldNames.FirstName]: user?.firstName,
                [FieldNames.LastName]: user?.lastName,
                [FieldNames.EmailAddress]: user?.emailAddress,
                [FieldNames.CityAndState]: user?.state,
              }}
              render={({ isValid, isSubmitting }) => {
                return (
                  <>
                    <div className={bem('fields')}>
                      <FormField name={FieldNames.FirstName} />
                      <FormField name={FieldNames.LastName} />
                      <FormField name={FieldNames.EmailAddress} />
                      <FormField name={FieldNames.CityAndState} />
                    </div>

                    {hasBillingAddress && (
                      <BillingAddressBox
                        user={user}
                        billingAddress={user.billingAddresses?.[0]}
                        onSuccess={handleEditAddSuccessBillingDetails}
                        onDelete={handleDeleteBillingDetails}
                      />
                    )}
                    {!hasBillingAddress && (
                      <div className={bem('billing-empty')} onClick={handleAddBillingAddress}>
                        <Button circle variant="secondary" className={bem('add-address-btn')}>
                          <SvgIcon Icon={PlusIcon} />
                        </Button>
                        <span>Add billing address</span>
                      </div>
                    )}

                    <div className={bem('actions')}>
                      <Button variant="default" onClick={() => cancelUserEditing()}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary" loading={isSubmitting} disabled={!isValid}>
                        Save
                      </Button>
                    </div>
                  </>
                );
              }}
            />
          </div>
        }
      />
    </Backdrop>
  );
}

export const UserSettings = withComponentErrorBoundary(withSuspense(UserSettingsComp, UserSettingsSkeleton));
